import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useHasMounted from 'hooks/useHasMounted'
import { useCallback, useContext, useEffect, useState, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { canBookNow, getCalendarMonths, getDaysOtherAvailability, getDurationOptions, getPackagesForFilters, getSelectedFixedFlashLengthOfStayOfferPageDay, getSelectedOfferPageDay, getSelectedPackageView, isFetchingCalendars } from 'selectors/offerPage/offerPageSelectors'
import OfferCalendarDayPicker from './DayPicker/OfferCalendarDayPicker'
import OfferCalendarMonthPicker from './MonthPicker/OfferCalendarMonthPicker'
import OfferCalendarNoFlightsMessage from './OfferCalendarNoFlightsMessage'
import OfferCalendarNotAvailableMessage from './OfferCalendarNotAvailableMessage'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DropdownSheetBody from 'LuxKit/Dropdown/Sheet/DropdownSheetBody'
import Caption from 'LuxKit/Typography/Caption'
import config from 'constants/config'
import ModalProvider from 'contexts/ModalProvider'
import { EmptyArray } from 'lib/array/arrayUtils'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import {
  getMultiRoomCalendarMonths,
  getMultiRoomDaysOtherAvailability,
  getMultiRoomPackagesForFilters,
  getSelectedOfferPageMultiRoomDay,
  isMultiRoomFetchingCalendars,
} from 'selectors/offerPage/multipleRoomBookingSelectors'
import OfferCalendarDatePickerDropdownHeader from './OfferCalendarDatePickerDropdownHeader'
import { CalendarBurnProvider } from './CalendarBurnContext'
import useCalendarBurnToggleVisible from './useCalendarBurnToggleVisible'
import OfferCalendarDatePickerDropdownFooter from './OfferCalendarDatePickerDropdownFooter'
import CalendarPickerRecommendations from './Recommendations/CalendarPickerRecommendations'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import { useWeddingGiftRegistryContext } from 'weddings/contexts/WeddingGiftRegistryContext'
import useFixedFlashLengthOfStayExperiment from './useFixedFlashLengthOfStayExperiment'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { shouldClearUnavailableCheckIn } from './fixedFlashLengthOfStayCalendarUtils'
import { getFirstAvailableDuration } from 'components/OfferPage/FlashHotel/flashHotelCalendarSkipUtils'
import moment from 'moment'

interface Props {
  offer: App.Offer
  checkIn?: string
  checkOut?: string
  fetching?: boolean
  selectedDay?: App.CalendarDay
  fixedFlashLengthOfStaySelectedDay?: App.CalendarDay
  flights?: boolean
  calendarError?: boolean
  calendarAvailability: boolean
  useRooms?: boolean
  defaultDayPicker?: boolean
  defaultMonth?: string
  duration: number
  relevantPackages: Array<App.OfferPackageOptionView>
  selectedPackageView?: App.OfferPackageOptionView
  recommendationsShown: boolean
  rooms?: Array<App.Occupants>
  otherAvailability: { [checkIn: string]: boolean }
  isAnyFlightsActive: boolean
  subFilters?: Array<OfferPageFilterType>
  scrollRef: React.RefObject<HTMLDivElement | null>
  canBookNow?: boolean
  shouldActivateFixedFlashLengthOfStayExperiment: boolean
  minimumDuration?: number
}

const DEFAULT_SUB_FILTERS: Array<OfferPageFilterType> = ['duration', 'airport', 'room']
function OfferCalendarDatePicker(props: Props) {
  const {
    checkIn,
    checkOut,
    offer,
    fetching,
    selectedDay,
    fixedFlashLengthOfStaySelectedDay,
    flights,
    calendarError,
    calendarAvailability,
    defaultMonth,
    defaultDayPicker,
    duration,
    relevantPackages,
    selectedPackageView,
    otherAvailability,
    rooms = EmptyArray,
    recommendationsShown,
    isAnyFlightsActive,
    subFilters = DEFAULT_SUB_FILTERS,
    scrollRef,
    canBookNow,
    shouldActivateFixedFlashLengthOfStayExperiment,
    minimumDuration,
  } = props

  const intl = useIntl()
  const isBurnToggleVisible = useCalendarBurnToggleVisible()
  const { isActive: isWeddingGiftRegistry } = useWeddingGiftRegistryContext()

  const isFixedFlashLengthOfStayOffer = isFlashHotelOffer(offer)
  // Desktop activates when the check-in filter dropdown opens. Mobile sidebar activates on
  // modal open (see OfferCalendarSidebarDatePicker). Keep activation scoped to calendar entry.
  const isFixedFlashLengthOfStayVariant = useFixedFlashLengthOfStayExperiment(isFixedFlashLengthOfStayOffer && shouldActivateFixedFlashLengthOfStayExperiment)
  const isFixedFlashLengthOfStayEnabled = isFixedFlashLengthOfStayOffer && isFixedFlashLengthOfStayVariant
  const calendarSubFilters = isFixedFlashLengthOfStayEnabled
    ? subFilters.filter(filter => filter !== 'duration')
    : subFilters
  const shouldShowFixedFlashLengthOfStayFromPrice = isFixedFlashLengthOfStayEnabled && !!checkIn && !checkOut
  const footerSelectedDay = shouldShowFixedFlashLengthOfStayFromPrice ? fixedFlashLengthOfStaySelectedDay : selectedDay
  const footerDuration = shouldShowFixedFlashLengthOfStayFromPrice && footerSelectedDay?.checkOut
    ? moment(footerSelectedDay.checkOut).diff(footerSelectedDay.checkIn, 'days')
    : duration
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const [monthIndex, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)
  const dispatch = useOfferPageDispatch()
  const mounted = useHasMounted()
  const clearHoveredCheckOutRef = useRef<(() => void) | undefined>(undefined)

  const registerClearHoveredCheckOut = useCallback((clearHoveredCheckOut?: () => void) => {
    clearHoveredCheckOutRef.current = clearHoveredCheckOut
  }, [])

  const clearHoveredCheckOut = useCallback(() => {
    clearHoveredCheckOutRef.current?.()
  }, [])

  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetStayUpgrade(selectedPackageView?.package)

  const selectedDayOtherAvailability = selectedDay ? otherAvailability[selectedDay.checkIn] : undefined

  useEffect(() => {
    if (shouldClearUnavailableCheckIn({
      isFixedFlashLengthOfStayEnabled,
      checkIn,
      checkOut,
      fetching,
      selectedDay,
      hasOtherAvailability: !!selectedDay && !!selectedDayOtherAvailability,
    })) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined })
    }
  }, [
    checkIn,
    checkOut,
    selectedDay,
    isFixedFlashLengthOfStayEnabled,
    fetching,
    selectedDayOtherAvailability,
    dispatch,
  ])

  const onDropdownClose = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, onUserAnalyticsAction])

  const onBackClick = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }

    setMonth(undefined)
  }, [setMonth, dispatch, recommendationsShown])

  const onBackToMonth = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }
    if (isFixedFlashLengthOfStayEnabled && minimumDuration !== undefined) {
      dispatch({ type: OfferPageActions.resetDatesToMinimumDuration, minimumDuration })
    } else {
      // Desktop clears both dates when returning to the month picker. Sidebar uses different
      // back-to-month rules for legacy vs new mobile hotel flows (see OfferCalendarSidebarDatePicker).
      dispatch({ type: OfferPageActions.resetDates })
    }
    clearHoveredCheckOut()
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    setMonth(undefined)
  }, [dispatch, isFixedFlashLengthOfStayEnabled, minimumDuration, onUserAnalyticsAction, recommendationsShown, clearHoveredCheckOut])

  // calendar reco on mobile is handled separately by OfferCalendarPickerRecommendationsModal
  const isMobile = useScreenSizeOnly('mobile')

  return (<ModalProvider>
    <CalendarBurnProvider>
      <OfferCalendarDatePickerDropdownHeader
        subFilters={calendarSubFilters}
        monthIndex={monthIndex}
        isCalendarBurnToggleVisible={isBurnToggleVisible}
        onBackClick={onBackClick}
        onDismissClick={onDropdownClose}
      />
      <DropdownSheetBody ref={scrollRef}>
        {recommendationsShown && !isMobile && (<CalendarPickerRecommendations
          offerId={offer.id}
          offerType={offer.type}
          rooms={rooms}
          heading={intl.formatMessage(
            {
              id: '2kg5LD4fnxQfhLWXEtQT',
              defaultMessage: 'Similar hotels to {hotelName}',
              description: 'Heading for similar hotel recommendations based on selected property',
            },
            { hotelName: offer.property?.name ?? offer.location },
          )}
          seedOffer={offer}
        />)}
        {!recommendationsShown && (
          <div style={isBurnToggleVisible ? { paddingTop: 12 } : undefined}>
            {mounted && !fetching && !calendarAvailability && <>
              {(calendarError || !flights) && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
              {!calendarError && flights && <OfferCalendarNoFlightsMessage canSwitchToHotel={!offer?.bundledWithFlightsOnly} />}
            </>}
            {calendarAvailability && <VerticalSpacer gap={24}>
              {isAnyFlightsActive && <Caption variant="medium" colour="light-foreground-alt">
                {intl.formatMessage(
                  {
                    id: 'l6wclXQC8CR1sd0sFcfo',
                    defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply',
                    description: 'Calendar pricing disclaimer for flights with brand terms and conditions',
                  },
                  { brandName: config.brandConfig.name },
                )}
              </Caption>}
              {monthIndex !== undefined && !fetching && <OfferCalendarDayPicker
                onBackToMonth={onBackToMonth}
                initialMonth={monthIndex}
                defaultDayPicker={defaultDayPicker}
                flightsOnly={offer.bundledWithFlightsOnly}
                isFixedFlashLengthOfStayEnabled={isFixedFlashLengthOfStayEnabled}
                contentRef={scrollRef}
                onRegisterClearHoveredCheckOut={registerClearHoveredCheckOut}
              />}
              {(monthIndex === undefined || fetching) && <OfferCalendarMonthPicker
                fetching={fetching}
                onMonthSelect={setMonth}
              />}
            </VerticalSpacer>}
          </div>
        )}
      </DropdownSheetBody>
      <OfferCalendarDatePickerDropdownFooter
        isSelectedTargetUpgradePackage={!!selectedTargetUpgradePackage}
        canBook={isWeddingGiftRegistry ? false : canBookNow}
        offer={offer}
        selectedDay={footerSelectedDay}
        duration={footerDuration}
        relevantPackages={relevantPackages}
        selectedPackageView={selectedPackageView}
        isAnyFlightsActive={isAnyFlightsActive}
        isOnMonthPicker={monthIndex === undefined}
        showFromPrice={shouldShowFixedFlashLengthOfStayFromPrice}
        isFixedFlashLengthOfStayEnabled={isFixedFlashLengthOfStayEnabled}
        minimumDuration={minimumDuration}
        onAfterResetDates={clearHoveredCheckOut}
      />
    </CalendarBurnProvider>
  </ModalProvider>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)

  return {
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    offer: state.offer,
    flights: state.flights,
    duration: state.duration,
    recommendationsShown: state.showRecommendationsView && shouldEnableCalendarRecommendations(state.offer),
    relevantPackages: isMultiRoom ? getMultiRoomPackagesForFilters(state, appState) : getPackagesForFilters(appState, state),
    selectedPackageView: getSelectedPackageView(state, appState),
    rooms: state.rooms,
    otherAvailability: isMultiRoom ? getMultiRoomDaysOtherAvailability(state, appState) : getDaysOtherAvailability(appState, state),
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    fixedFlashLengthOfStaySelectedDay: isMultiRoom ? undefined : getSelectedFixedFlashLengthOfStayOfferPageDay(appState, state),
    fetching: isMultiRoom ? isMultiRoomFetchingCalendars(state, appState) : isFetchingCalendars(appState, state),
    calendarAvailability: isMultiRoom ? getMultiRoomCalendarMonths(state, appState).availableCount > 0 : getCalendarMonths(appState, state).availableCount > 0,
    calendarError: !!state.offer?.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    isAnyFlightsActive: state.flights,
    canBookNow: canBookNow(state, appState),
    // Desktop: activate when the check-in filter opens (mobile sidebar activates on modal open).
    shouldActivateFixedFlashLengthOfStayExperiment: state.activeFilter === 'checkin' || state.activeHeaderFilter === 'checkin',
    minimumDuration: getFirstAvailableDuration(getDurationOptions(state, appState)),
    open: !!state.activeFilter,
  }
}, OfferCalendarDatePicker, preventRenderWithClosedFilter)
